import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: 90, suffix: "+", label: "Renders Delivered", testId: "stats-renders" },
  { value: 20, suffix: "+", label: "Building Designs", testId: "stats-designs" },
  { value: 15, suffix: "+", label: "Interior Works", testId: "stats-interiors" },
  { value: 0, display: "4K/8K", label: "Photoreal Outputs", testId: "stats-quality" },
];

const TOOLS = ["AutoCAD", "SketchUp", "Lumion", "Revit", "Photoshop"];

const Counter = ({ target, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return <span ref={ref}>{n}{suffix}</span>;
};

export const About = () => {
  return (
    <section id="about" className="relative bg-[#F4F1F5] bg-dots-light">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          <span className="text-[#c85a54] text-xs md:text-sm tracking-[0.3em] uppercase font-semibold">
            About The Studio
          </span>
          <h2 className="mt-5 font-display font-black uppercase tracking-tight leading-[0.98] text-[#1A1518] text-4xl md:text-5xl lg:text-6xl text-balance">
            We build spaces before they exist — <span className="brand-gradient-text">frame by frame.</span>
          </h2>
          <p className="mt-7 text-[#4D454A] text-base md:text-lg leading-relaxed max-w-3xl">
            We bring together <span className="font-semibold text-[#1A1518]">architecture, interiors, visualization and technical expertise</span> to transform ideas into well-designed, buildable spaces. From residential homes and commercial developments to refined interiors and detailed working drawings, we approach every project with a strong focus on <span className="font-semibold text-[#1A1518]">design, functionality, materiality and execution</span>.
          </p>
        </motion.div>

        {/* Decorative divider */}
        <div className="mt-16 flex items-center gap-4">
          <div className="h-px flex-1 bg-[#E5DEE5]" />
          <div className="w-2 h-2 rounded-full brand-gradient" />
          <div className="h-px flex-1 bg-[#E5DEE5]" />
        </div>

        {/* Stats row */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5DEE5] rounded-2xl overflow-hidden border border-[#E5DEE5]">
          {STATS.map((s, i) => (
            <motion.div
              key={s.testId}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#FDFCFD] p-8 md:p-10"
            >
              <div className="font-display font-black text-[#4a1c40] text-4xl md:text-5xl tracking-tight">
                {s.display ? s.display : <Counter target={s.value} suffix={s.suffix} />}
              </div>
              <div className="mt-3 text-[#4D454A] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tooling banner */}
        <div className="mt-10 rounded-2xl bg-[#1A1518] overflow-hidden">
          <div className="flex items-center gap-6 px-6 py-5 flex-wrap justify-center">
            <span className="text-white/50 text-[11px] tracking-[0.3em] uppercase">Core Tech Stack</span>
            <div className="hidden sm:block h-4 w-px bg-white/20" />
            <div className="flex items-center gap-4 md:gap-8 flex-wrap justify-center">
              {TOOLS.map((t, i) => (
                <React.Fragment key={t}>
                  {i > 0 && <span className="text-white/25">|</span>}
                  <span className="text-white font-display font-semibold tracking-wide text-sm md:text-base">
                    {t}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
